import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { apiFetch } from '../utils/apiFetch';
import { isStoredSessionTokenCurrent } from '../utils/auth';

export const getNinetyNineFoodPickupInfo = (order = {}) => {
  const provider = order.ninetyNineFood || {};
  const type = Number(order.deliveryType ?? provider.deliveryType);
  if (type === 1 && String(provider.handoverCode || '').trim()) return { code: String(provider.handoverCode).trim(), label: 'Código de coleta 99Food' };
  if (type === 0 && String(provider.takeawayCode || '').trim()) return { code: String(provider.takeawayCode).trim(), label: 'Código de retirada 99Food' };
  return { code: '', label: '' };
};

export const getNinetyNineFoodActionPolicy = (order = {}) => {
  const provider = order.ninetyNineFood || {};
  const status = Number(order.officialStatus ?? provider.officialStatus);
  const deliveryType = Number(order.deliveryType ?? provider.deliveryType);
  const active = status >= 200 && status < 600;
  const payMethod = order.payMethod ?? provider.payMethod;
  const offlineCash = payMethod === 2
    || ((payMethod === null || payMethod === undefined) && Number(order.payType ?? provider.payType) === 2);
  const external99 = Boolean(provider.entrega?.operationId || provider.externalCourier?.operationId);
  return {
    ready: active && order.status === 'preparing' && order.requiresAcceptance !== true,
    finish: active && deliveryType === 0 && (provider.kitchenReady === true || order.status === 'ready'),
    delivered: active && deliveryType === 2 && !external99,
    verifyDeliveryCode: active && deliveryType === 2 && !external99,
    cancel: status >= 100 && status < 600,
    payConfirm: status === 200 && deliveryType === 1
      && offlineCash
      && (Number(order.payChannel ?? provider.payChannel) === 153 || Number(order.payType ?? provider.payType) === 2)
      && Number(order.shopPaidMoney ?? provider.shopPaidMoney) > 0
      && order.payments?.provider?.shopPaymentReceiptConfirmed !== true,
  };
};

export const NINETY_NINE_FOOD_CANCEL_REASON_LABELS = {
  1010: 'Item esgotado', 1020: 'Loja fechada', 1030: 'Loja muito ocupada',
  1040: 'Acidente ou falta de água / energia', 1050: 'Problema do cliente',
  1060: 'Sem entregador disponível', 1070: 'Atualizar cardápio', 1071: 'Fora da área de entrega',
  1072: 'Área insegura', 1073: 'Suspeita de fraude ou trote', 1074: 'Dúvidas sobre taxas ou promoções',
  1080: 'Outro motivo',
};
const CANCEL_REASONS = [1010, 1020, 1030, 1040, 1050, 1070, 1073, 1074, 1080];
export const getNinetyNineFoodCancelReasons = (order = {}) => (
  Number(order.deliveryType) === 2
    ? [...CANCEL_REASONS, 1060, 1071, 1072].sort((a, b) => a - b)
    : CANCEL_REASONS
);

export const buildNinetyNineFoodDecision = (kind, request, agree, selectedReason) => {
  if (!request?.applyId) throw new Error('Solicitação sem identificador oficial. Atualize o pedido.');
  if (agree) return { applyId: String(request.applyId), agree: true };
  const reasons = Array.isArray(request.reasons) ? request.reasons : [];
  const selected = reasons[Number(selectedReason)];
  if (!selected || selectedReason === '') throw new Error('Selecione uma razão oficial para recusar.');
  if (kind === 'applyCancel') {
    if (!selected.reason) throw new Error('Razão de recusa indisponível. Atualize o pedido.');
    return { applyId: String(request.applyId), agree: false, reason: String(selected.reason) };
  }
  if (selected.baseReasonId === undefined || !selected.baseReason) {
    throw new Error('Razão oficial de reembolso incompleta. Atualize o pedido.');
  }
  return {
    applyId: String(request.applyId), agree: false,
    baseReasonId: selected.baseReasonId, baseReason: String(selected.baseReason),
  };
};

const NinetyNineFoodOrderActions = ({ apiUrl, order, sessionToken, onUpdated, requestedAction }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [reasonId, setReasonId] = useState('');
  const [reason, setReason] = useState('');
  const [deliveryCode, setDeliveryCode] = useState('');
  const [requestReasons, setRequestReasons] = useState({});
  const current = useRef({ orderId: order.orderId, sessionToken, mounted: true });
  current.current = { orderId: order.orderId, sessionToken, mounted: true };
  useEffect(() => {
    setBusy(false); setError(''); setNotice(''); setConfirmation(null); setRequestReasons({});
    return () => { current.current.mounted = false; };
  }, [order.orderId, sessionToken]);
  const isCurrent = () => current.current.mounted && current.current.orderId === order.orderId
    && current.current.sessionToken === sessionToken && isStoredSessionTokenCurrent(sessionToken);
  const policy = getNinetyNineFoodActionPolicy(order);
  const pickupInfo = getNinetyNineFoodPickupInfo(order);
  const handledRequest=useRef(null);
  useEffect(()=>{
    if(!requestedAction?.id||requestedAction.orderId!==order.orderId||handledRequest.current===requestedAction.id) return;
    const action=requestedAction.action;
    if(action==='refresh') {handledRequest.current=requestedAction.id;return;}
    if(action==='verifyDeliveryCode'&&!policy.verifyDeliveryCode) return;
    if(action==='finish'&&!policy.finish) return;
    if(action==='cancel'&&!policy.cancel) return;
    handledRequest.current=requestedAction.id;
    setError('');setReasonId('');setReason('');setDeliveryCode('');
    setConfirmation({action,title:action==='cancel'?'Solicitar cancelamento na 99Food?':action==='finish'?'Confirmar retirada do cliente?':'Confirmar entrega com o código da 99Food?',payload:{}});
  },[requestedAction,order.orderId,policy.verifyDeliveryCode,policy.finish,policy.cancel]);


  const execute = async (action, payload = {}) => {
    if (busy || !isCurrent()) return;
    if(action==='verifyDeliveryCode'&&!getNinetyNineFoodActionPolicy(order).verifyDeliveryCode) {setConfirmation(null);return;}
    setBusy(true); setError(''); setNotice('');
    try {
      const operationPath = action === 'refresh' ? 'refresh' : `actions/${action}`;
      const response = await apiFetch(`${apiUrl}/api/99food/orders/${encodeURIComponent(order.orderId)}/${operationPath}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.order) throw new Error(data.error || 'A 99Food não confirmou esta operação.');
      if (!isCurrent()) return;
      onUpdated?.(data.order);
      setConfirmation(null);
      setNotice(data.pending ? 'Solicitação enviada. Aguardando confirmação da 99Food.' : 'Pedido atualizado pela 99Food.');
    } catch (failure) {
      if (isCurrent()) setError(failure.message);
    } finally {
      if (isCurrent()) setBusy(false);
    }
  };

  const ask = (action, title, payload = {}) => {
    setError(''); setReasonId(''); setReason(''); setDeliveryCode(''); setConfirmation({ action, title, payload });
  };
  const decide = (kind, request, agree) => {
    try {
      const key = `${kind}:${request.applyId}`;
      const payload = buildNinetyNineFoodDecision(kind, request, agree, requestReasons[key] ?? '');
      ask(kind, `${agree ? 'Aceitar' : 'Recusar'} ${kind === 'applyCancel' ? 'cancelamento' : 'reembolso'} solicitado?`, payload);
    } catch (failure) { setError(failure.message); }
  };

  return (
    <Box sx={{ my: 2 }} data-testid="99food-order-actions">
      {pickupInfo.code && <Alert severity="info" sx={{ mb: 1 }}>{pickupInfo.label}: <strong>{pickupInfo.code}</strong></Alert>}
      {Number(order.deliveryType) === 1 && (
        <Alert severity="info" sx={{ mb: 1 }}>Entrega realizada pela 99Food. Saída e conclusão são atualizadas pela plataforma.</Alert>
      )}
      {Number(order.deliveryType) === 2 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Entrega própria: acompanhe a operação pelo Brutos Entregas.</Typography>
      )}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 1 }}>{notice}</Alert>}
      {order.ninetyNineFood?.officialSyncPending===true && order.ninetyNineFood?.lastAction?.state!=='unknown' && <Alert severity="info" sx={{mb:1}}>Etapa local atualizada. A confirmação da 99Food está pendente.</Alert>}
      {order.ninetyNineFood?.lastAction?.state === 'unknown' && <Alert severity="warning" sx={{ mb: 1 }}>O resultado de uma ação anterior ainda é incerto. Consulte o estado oficial antes de continuar.</Alert>}
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>
        <Button disabled={busy} onClick={() => execute('refresh')}>Consultar estado na 99Food</Button>
        {policy.ready && <Button disabled={busy} variant="outlined" onClick={() => ask('ready', 'Confirmar pedido pronto na 99Food?')}>Marcar pronto</Button>}
        {policy.finish && <Button disabled={busy} variant="outlined" onClick={() => ask('finish', 'Confirmar que o cliente retirou o pedido?')}>Confirmar retirada</Button>}
        {policy.verifyDeliveryCode && <Button disabled={busy} variant="contained" onClick={() => ask('verifyDeliveryCode', 'Confirmar entrega com o código da 99Food?')}>Confirmar com código 99Food</Button>}
        {policy.delivered && <Button disabled={busy} variant="outlined" onClick={() => ask('delivered', 'Confirmar que a entrega própria foi concluída?')}>Confirmar entrega manual</Button>}
        {policy.payConfirm && <Button disabled={busy} variant="outlined" onClick={() => ask('payConfirm', 'Confirmar que a loja recebeu o dinheiro do entregador 99Food?', { received: true })}>Confirmar recebimento do entregador</Button>}
        {policy.cancel && <Button disabled={busy} color="error" onClick={() => ask('cancel', 'Solicitar cancelamento na 99Food?')}>Cancelar na 99Food</Button>}
      </Stack>
      {[
        ['applyCancel', 'Cancelamento solicitado pelo cliente', order.cancellationRequests],
        ['applyRefund', 'Reembolso solicitado pelo cliente', order.refundRequests],
      ].flatMap(([kind, title, requests]) => (Array.isArray(requests) ? requests : [])
        .filter((request) => request.status === 'pending')
        .map((request) => {
          const key = `${kind}:${request.applyId}`;
          return (
            <Box key={key} sx={{ mt: 2, p: 1.5, border: '1px solid', borderColor: 'warning.main', borderRadius: 1 }}>
              <Typography variant="subtitle2">{title}</Typography>
              <Typography variant="body2">Solicitação {String(request.applyId)}</Typography>
              {(request.reason || request.applyReason) && <Typography variant="body2">{request.reason || request.applyReason}</Typography>}
              <TextField select fullWidth size="small" label="Razão oficial para recusar" sx={{ my: 1 }}
                value={requestReasons[key] ?? ''} onChange={(event) => setRequestReasons((old) => ({ ...old, [key]: event.target.value }))}>
                {(request.reasons || []).map((option, index) => <MenuItem key={index} value={String(index)}>{option.reason || option.baseReason}</MenuItem>)}
              </TextField>
              <Stack direction="row" spacing={1}>
                <Button disabled={busy} onClick={() => decide(kind, request, true)}>Aceitar solicitação</Button>
                <Button disabled={busy || !(request.reasons || []).length} color="error" onClick={() => decide(kind, request, false)}>Recusar solicitação</Button>
              </Stack>
            </Box>
          );
        }))}
      <Dialog open={Boolean(confirmation)} onClose={() => { if (!busy) setConfirmation(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{confirmation?.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Esta ação será enviada à 99Food para o pedido {order.displayId || order.externalOrderId}.</Typography>
          {confirmation?.action === 'verifyDeliveryCode' && <TextField autoFocus fullWidth label="Código de entrega 99Food" value={deliveryCode} sx={{ mt: 2 }} onChange={(event) => setDeliveryCode(event.target.value.replace(/\D/g, '').slice(0, 4))} inputProps={{ inputMode: 'numeric', pattern: '[0-9]{4}', maxLength: 4 }} helperText="Informe os quatro dígitos fornecidos pelo cliente na 99Food." />}
          {confirmation?.action === 'payConfirm' && <Alert severity="warning" sx={{ mt: 2 }}>Confirme somente após receber e conferir o valor de {Number(order.shopPaidMoney ?? order.ninetyNineFood?.shopPaidMoney).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}.</Alert>}
          {confirmation?.action === 'cancel' && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField select fullWidth label="Motivo oficial" value={reasonId} onChange={(event) => setReasonId(event.target.value)}>
                {getNinetyNineFoodCancelReasons(order).map((code) => <MenuItem key={code} value={String(code)}>{NINETY_NINE_FOOD_CANCEL_REASON_LABELS[code]}</MenuItem>)}
              </TextField>
              <TextField fullWidth multiline minRows={2} label="Justificativa" value={reason} onChange={(event) => setReason(event.target.value)} required={reasonId === '1080'} inputProps={{ maxLength: 500 }} />
            </Stack>
          )}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setConfirmation(null)}>Voltar</Button>
          <Button disabled={busy || (confirmation?.action === 'verifyDeliveryCode' && !/^\d{4}$/.test(deliveryCode)) || (confirmation?.action === 'cancel' && (!reasonId || (reasonId === '1080' && !reason.trim())))} variant="contained"
            onClick={() => execute(confirmation.action, confirmation.action === 'cancel' ? { reasonId: Number(reasonId), reason: reason.trim() } : confirmation.action === 'verifyDeliveryCode' ? { deliveryCode } : confirmation.payload)}>
            {busy ? 'Enviando…' : 'Confirmar ação'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NinetyNineFoodOrderActions;
